import type { FormInstance } from "antd";
import { isProblemDetails, problemMessages, type ProblemDetails } from "@qorakul/shared/api";
import { retryAfterSeconds } from "./retry-after.ts";

export type RequestFailure = Error & { problem: ProblemDetails | undefined; status: number; retryAfterSeconds: number | undefined };

/**
 * Turns a failed openapi-fetch call into a throwable that keeps the problem details for the form. Pass the response
 * headers where a 429/423 is possible so the wait can also come from `Retry-After`.
 */
export function requestFailure(error: unknown, status: number, headers?: Headers): RequestFailure {
  return Object.assign(new Error(String(status)), {
    problem: isProblemDetails(error) ? error : undefined,
    status,
    retryAfterSeconds: retryAfterSeconds(error, headers),
  });
}

/**
 * Puts RFC 9457 field errors on the matching form fields and returns the messages that belong to no field
 * (or the title when there are none) for a summary alert.
 */
export function applyProblemToForm(failure: RequestFailure, form: FormInstance, fields: readonly string[], fallback: string): string[] {
  const errors = failure.problem?.errors ?? {};
  const unmatched: string[] = [];
  const matched: { name: string; errors: string[] }[] = [];
  for (const [key, messages] of Object.entries(errors)) {
    if (fields.includes(key)) matched.push({ name: key, errors: messages });
    else unmatched.push(...messages);
  }
  if (matched.length > 0) form.setFields(matched);
  if (unmatched.length > 0) return unmatched;
  return matched.length > 0 ? [] : problemMessages(failure.problem, fallback);
}
